import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getPatentDetails } from '@/api/patentViewer.js'
import { logger } from '@moatmetrics/vue-logger'

export const usePatentViewerStore = defineStore('patentViewer', () => {
    const nodeIdList = ref([])
    const entityPkList = ref([])
    const showPatentViewerList = ref(false)
    const entityName = ref('')
    const entityPk = ref(null)
    const technologyName = ref('')
    const targetPatentId = ref(null)
    const patentDetailsOpen = ref(false)
    const patentDetails = ref(null)
    const listViewType = ref(null)
    const ipBasicsObject = ref(null)
    const ipBasicsEntityPK = ref(null)
    const cachedPatentDetails = ref({})
    const insertionOrder = ref([])
    const maxPatentCache = ref(30)
    const hideMappedProducts = ref(false)

    // Similar Patents
    const cachedSimilarPatents = ref({})
    const similarPatentsInsertionOrder = ref([])

    // AI Summary
    const cachedAISummaries = ref({})
    const aiSummaryInsertionOrder = ref([])

    // Mapped Products
    const cachedMappedProducts = ref({})
    const mappedProductsInsertionOrder = ref([])

    const resetState = () => {
        nodeIdList.value = []
        entityPkList.value = []
        showPatentViewerList.value = false
        entityName.value = ''
        entityPk.value = null
        technologyName.value = ''
        targetPatentId.value = null
        patentDetailsOpen.value = false
        patentDetails.value = null
        listViewType.value = null
        ipBasicsObject.value = null
        ipBasicsEntityPK.value = null
        cachedPatentDetails.value = {}
        insertionOrder.value = []
        cachedSimilarPatents.value = {}
        similarPatentsInsertionOrder.value = []
        cachedAISummaries.value = {}
        aiSummaryInsertionOrder.value = []
        hideMappedProducts.value = false
    }

    const setTargetPatentId = (patentId) => {
        targetPatentId.value = patentId
    }

    const setTargetPatent = async (patentId) => {
        if (patentId) {
            setTargetPatentId(patentId)
            await fetchPatentDetails(patentId)
        }
    }

    const fetchPatentDetails = async (patentId) => {
        if (
            patentExistsInCache &&
            cachedPatentDetails.value[patentId] &&
            !cachedPatentDetails.value[patentId].isRowData &&
            patentId === targetPatentId.value
        ) {
            patentDetails.value = cachedPatentDetails.value[patentId]
            return
        }

        try {
            const { data } = await getPatentDetails(patentId)
            setCachedPatentDetail(patentId, data)
        } catch (error) {
            logger.error('Error fetching patent details:', error)
        }
    }

    const patentExistsInCache = (patentId) => {
        return cachedPatentDetails.value[patentId]
    }

    const setCachedPatentRowDetail = (patentId, rowDetail) => {
        if (patentId === targetPatentId.value) {
            patentDetails.value = rowDetail
        }
        updatePatentDetailCache(patentId, rowDetail)
    }

    const setCachedPatentDetail = (patentId, detail) => {
        if (patentId === targetPatentId.value) {
            patentDetails.value = detail
        }
        updatePatentDetailCache(patentId, detail)
    }

    const updatePatentDetailCache = (patentId, detail) => {
        if (!cachedPatentDetails.value[patentId]) {
            insertionOrder.value.push(patentId)
        } else {
            const index = insertionOrder.value.indexOf(patentId)
            if (index !== -1) {
                insertionOrder.value.splice(index, 1)
                insertionOrder.value.push(patentId)
            }
        }

        cachedPatentDetails.value[patentId] = detail
        while (Object.keys(cachedPatentDetails.value).length > maxPatentCache.value) {
            const oldestKey = insertionOrder.value.shift()
            delete cachedPatentDetails.value[oldestKey]
        }
    }

    // Similar Patents Cache
    const similarPatentsExistInCache = (patentId) => {
        return cachedSimilarPatents.value[patentId] !== undefined
    }

    const setCachedSimilarPatents = (patentId, similarPatents) => {
        updateSimilarPatentsCache(patentId, similarPatents)
    }

    const getCachedSimilarPatents = (patentId) => {
        return cachedSimilarPatents.value[patentId]
    }

    const updateSimilarPatentsCache = (patentId, similarPatents) => {
        if (!cachedSimilarPatents.value[patentId]) {
            similarPatentsInsertionOrder.value.push(patentId)
        } else {
            const index = similarPatentsInsertionOrder.value.indexOf(patentId)
            if (index !== -1) {
                similarPatentsInsertionOrder.value.splice(index, 1)
                similarPatentsInsertionOrder.value.push(patentId)
            }
        }

        cachedSimilarPatents.value[patentId] = similarPatents

        while (Object.keys(cachedSimilarPatents.value).length > maxPatentCache.value) {
            const oldestKey = similarPatentsInsertionOrder.value.shift()
            delete cachedSimilarPatents.value[oldestKey]
        }
    }

    const removeCachedSimilarPatents = (patentId) => {
        if (cachedSimilarPatents.value[patentId]) {
            delete cachedSimilarPatents.value[patentId]
            // Also remove from insertion order if it exists
            const index = similarPatentsInsertionOrder.value.indexOf(patentId)
            if (index !== -1) {
                similarPatentsInsertionOrder.value.splice(index, 1)
            }
        }
    }

    // AI Summary Cache
    const aiSummaryExistsInCache = (patentId) => {
        return cachedAISummaries.value[patentId] !== undefined
    }

    const setCachedAISummary = (patentId, aiSummary) => {
        updateAISummaryCache(patentId, aiSummary)
    }

    const getCachedAISummary = (patentId) => {
        return cachedAISummaries.value[patentId]
    }

    const updateAISummaryCache = (patentId, aiSummary) => {
        if (!cachedAISummaries.value[patentId]) {
            aiSummaryInsertionOrder.value.push(patentId)
        } else {
            const index = aiSummaryInsertionOrder.value.indexOf(patentId)
            if (index !== -1) {
                aiSummaryInsertionOrder.value.splice(index, 1)
                aiSummaryInsertionOrder.value.push(patentId)
            }
        }

        cachedAISummaries.value[patentId] = aiSummary

        while (Object.keys(cachedAISummaries.value).length > maxPatentCache.value) {
            const oldestKey = aiSummaryInsertionOrder.value.shift()
            delete cachedAISummaries.value[oldestKey]
        }
    }

    // Mapped Products Cache
    const mappedProductsExistInCache = (patentId) => {
        return cachedMappedProducts.value[patentId] !== undefined
    }

    const setCachedMappedProducts = (patentId, mappedProducts) => {
        updateMappedProductsCache(patentId, mappedProducts)
    }

    const getCachedMappedProducts = (patentId) => {
        return cachedMappedProducts.value[patentId]
    }

    const updateMappedProductsCache = (patentId, mappedProducts) => {
        if (!cachedMappedProducts.value[patentId]) {
            mappedProductsInsertionOrder.value.push(patentId)
        } else {
            const index = mappedProductsInsertionOrder.value.indexOf(patentId)
            if (index !== -1) {
                mappedProductsInsertionOrder.value.splice(index, 1)
                mappedProductsInsertionOrder.value.push(patentId)
            }
        }

        cachedMappedProducts.value[patentId] = mappedProducts

        while (Object.keys(cachedMappedProducts.value).length > maxPatentCache.value) {
            const oldestKey = mappedProductsInsertionOrder.value.shift()
            delete cachedMappedProducts.value[oldestKey]
        }
    }

    const loadingPatentDetail = computed(() => {
        return (
            targetPatentId?.value === patentDetails?.value?.publicationPk &&
            patentDetails?.value?.isRowData
        )
    })

    const setHideMappedProducts = (hide) => {
        hideMappedProducts.value = hide
    }

    return {
        nodeIdList,
        entityPkList,
        showPatentViewerList,
        entityName,
        technologyName,
        targetPatentId,
        patentDetailsOpen,
        patentDetails,
        listViewType,
        ipBasicsObject,
        ipBasicsEntityPK,
        cachedPatentDetails,
        insertionOrder,
        resetState,
        fetchPatentDetails,
        setTargetPatent,
        patentExistsInCache,
        setCachedPatentDetail,
        setCachedPatentRowDetail,
        setTargetPatentId,
        loadingPatentDetail,
        cachedSimilarPatents,
        similarPatentsInsertionOrder,
        similarPatentsExistInCache,
        setCachedSimilarPatents,
        getCachedSimilarPatents,
        removeCachedSimilarPatents,
        cachedAISummaries,
        aiSummaryInsertionOrder,
        aiSummaryExistsInCache,
        setCachedAISummary,
        getCachedAISummary,
        cachedMappedProducts,
        mappedProductsInsertionOrder,
        mappedProductsExistInCache,
        setCachedMappedProducts,
        getCachedMappedProducts,
        setHideMappedProducts,
        hideMappedProducts,
    }
})
